import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { EnhancedDeal, Pipeline } from '../../types';
import { useCRM } from '../../utils/CRMContext';
import { computeThreatScore, getTopThreats, ThreatAssessment } from '../../utils/sniperScoring';
import { X, Crosshair, Zap, ArrowUpRight, ChevronRight, Trash2, LayoutDashboard } from 'lucide-react';
import './SniperZone.css';

interface SniperZoneProps {
  pipeline: Pipeline;
  allDeals: EnhancedDeal[];
  onClose: () => void;
  onDealClick: (deal: EnhancedDeal) => void;
}

type SortMode = 'threat' | 'value';

interface ScoredDeal {
  deal: EnhancedDeal;
  assessment: ThreatAssessment;
  daysInStage: number;
  stageName: string;
}

function getDaysFromDate(date: Date | string | undefined): number {
  if (!date) return 0;
  const d = new Date(date);
  if (isNaN(d.getTime())) return 0;
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  return days < 0 ? 0 : days;
}

const SniperZone: React.FC<SniperZoneProps> = ({ pipeline, allDeals, onClose, onDealClick }) => {
  const navigate = useNavigate();
  const { updateEnhancedDeal } = useCRM();
  const [sortMode, setSortMode] = useState<SortMode>('threat');

  const sniperDeals: ScoredDeal[] = useMemo(() => {
    const flagged = allDeals.filter(d => d.sniperZone && d.outcome === 'active');
    const totalStages = pipeline.stages.length || 1;

    return flagged.map(deal => {
      const stage = pipeline.stages.find(s => s.id === deal.stageId);
      return {
        deal,
        assessment: computeThreatScore(deal, stage, totalStages, allDeals),
        daysInStage: getDaysFromDate(deal.currentStageEnteredAt || deal.createdAt),
        stageName: stage?.name || 'Unknown',
      };
    }).sort((a, b) => {
      if (sortMode === 'value') return b.deal.value - a.deal.value;
      return b.assessment.score - a.assessment.score;
    });
  }, [allDeals, pipeline, sortMode]);

  const totalValueAtRisk = useMemo(() =>
    sniperDeals.reduce((sum, s) => sum + s.deal.value * (s.deal.probability / 100), 0),
    [sniperDeals],
  );

  const avgThreat = useMemo(() => {
    if (sniperDeals.length === 0) return 0;
    return Math.round(sniperDeals.reduce((s, d) => s + d.assessment.score, 0) / sniperDeals.length);
  }, [sniperDeals]);

  const handleRemove = useCallback(async (dealId: string) => {
    await updateEnhancedDeal(dealId, { sniperZone: false } as any);
  }, [updateEnhancedDeal]);

  const handleAutoPopulate = useCallback(async () => {
    const top = getTopThreats(allDeals, pipeline.stages, 5);
    for (const { deal } of top) {
      if (deal.id && !deal.sniperZone) {
        await updateEnhancedDeal(deal.id, { sniperZone: true } as any);
      }
    }
  }, [allDeals, pipeline.stages, updateEnhancedDeal]);

  const formatCurrency = (v: number) => {
    if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
    return `$${v.toLocaleString()}`;
  };

  const getBandColor = (band: ThreatAssessment['band']) => {
    switch (band) {
      case 'critical': return '#ef4444';
      case 'high': return '#f59e0b';
      case 'moderate': return '#3b82f6';
      case 'low': return '#22c55e';
    }
  };

  const getBandLabel = (band: ThreatAssessment['band']) => {
    switch (band) {
      case 'critical': return 'CRITICAL';
      case 'high': return 'HIGH';
      case 'moderate': return 'MODERATE';
      case 'low': return 'LOW';
    }
  };

  return (
    <div className="sz-overlay" onClick={onClose}>
      <div className="sz-panel" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="sz-header">
          <div className="sz-header-left">
            <div className="sz-icon-wrap">
              <Crosshair size={18} />
            </div>
            <div>
              <h2 className="sz-title">Sniper Zone</h2>
              <span className="sz-subtitle">Deals requiring immediate focus</span>
            </div>
          </div>
          <button className="sz-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Stats strip */}
        <div className="sz-stats">
          <div className="sz-stat">
            <span className="sz-stat-num">{sniperDeals.length}</span>
            <span className="sz-stat-label">Tracked</span>
          </div>
          <div className="sz-stat-div" />
          <div className="sz-stat">
            <span className="sz-stat-num">{formatCurrency(totalValueAtRisk)}</span>
            <span className="sz-stat-label">Value at Risk</span>
          </div>
          <div className="sz-stat-div" />
          <div className="sz-stat">
            <span className="sz-stat-num sz-stat-threat" data-band={avgThreat >= 75 ? 'critical' : avgThreat >= 50 ? 'high' : avgThreat >= 30 ? 'moderate' : 'low'}>
              {avgThreat}
            </span>
            <span className="sz-stat-label">Avg Threat</span>
          </div>
        </div>

        {/* Controls */}
        <div className="sz-controls">
          <div className="sz-sort-pills">
            <button
              className={`sz-pill ${sortMode === 'threat' ? 'sz-pill-active' : ''}`}
              onClick={() => setSortMode('threat')}
            >
              Threat Score
            </button>
            <button
              className={`sz-pill ${sortMode === 'value' ? 'sz-pill-active' : ''}`}
              onClick={() => setSortMode('value')}
            >
              Deal Value
            </button>
          </div>
          <button className="sz-auto-btn" onClick={handleAutoPopulate} title="Auto-detect top 5 at-risk deals">
            <Zap size={13} />
            Auto-detect
          </button>
        </div>

        {/* Deal list */}
        <div className="sz-list">
          {sniperDeals.length === 0 ? (
            <div className="sz-empty">
              <Crosshair size={32} className="sz-empty-icon" />
              <p className="sz-empty-title">No deals in the sniper zone</p>
              <p className="sz-empty-hint">Add deals from the pipeline or use auto-detect to find the most at-risk opportunities.</p>
              <button className="sz-auto-btn sz-auto-btn-lg" onClick={handleAutoPopulate}>
                <Zap size={14} />
                Auto-detect top threats
              </button>
            </div>
          ) : (
            sniperDeals.map(({ deal, assessment, daysInStage, stageName }) => (
              <div
                key={deal.id}
                className="sz-row"
                onClick={() => { onDealClick(deal); onClose(); }}
              >
                {/* Threat gauge */}
                <div className="sz-threat-col">
                  <div
                    className="sz-threat-ring"
                    style={{
                      '--threat-color': getBandColor(assessment.band),
                      '--threat-pct': `${assessment.score}%`,
                    } as React.CSSProperties}
                  >
                    <span className="sz-threat-num">{assessment.score}</span>
                  </div>
                  <span className="sz-threat-label" style={{ color: getBandColor(assessment.band) }}>
                    {getBandLabel(assessment.band)}
                  </span>
                </div>

                {/* Deal info */}
                <div className="sz-deal-info">
                  <div className="sz-deal-title-row">
                    <span className="sz-deal-name">{deal.title}</span>
                    <span className="sz-deal-value">{formatCurrency(deal.value)}</span>
                  </div>
                  <div className="sz-deal-meta">
                    <span className="sz-deal-contact">{deal.contactName}</span>
                    <span className="sz-meta-sep" />
                    <span className="sz-deal-stage">{stageName}</span>
                    <span className="sz-meta-sep" />
                    <span className={`sz-deal-days ${daysInStage > (pipeline.stages.find(s => s.id === deal.stageId)?.rottingDays ?? 30) ? 'sz-days-over' : ''}`}>
                      {daysInStage}d
                    </span>
                    <span className="sz-meta-sep" />
                    <span className="sz-deal-prob">{deal.probability}%</span>
                  </div>

                  {/* Signal bars */}
                  <div className="sz-signals">
                    {assessment.signals.map(sig => (
                      <div key={sig.label} className="sz-signal" title={sig.detail}>
                        <span className="sz-signal-name">{sig.label}</span>
                        <div className="sz-signal-bar">
                          <div
                            className="sz-signal-fill"
                            style={{
                              width: `${sig.value}%`,
                              background: sig.value >= 70 ? '#ef4444' : sig.value >= 40 ? '#f59e0b' : '#3b82f6',
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="sz-row-actions" onClick={e => e.stopPropagation()}>
                  {deal.leadId && (
                    <button
                      className="sz-row-btn"
                      onClick={() => { navigate(`/client/${deal.leadId}`); onClose(); }}
                      title="Client Portal"
                    >
                      <LayoutDashboard size={14} />
                    </button>
                  )}
                  <button
                    className="sz-row-btn"
                    onClick={() => { onDealClick(deal); onClose(); }}
                    title="Open Deal"
                  >
                    <ArrowUpRight size={14} />
                  </button>
                  <button
                    className="sz-row-btn sz-row-btn-remove"
                    onClick={() => deal.id && handleRemove(deal.id)}
                    title="Remove from Sniper Zone"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>

                <ChevronRight size={14} className="sz-row-arrow" />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default SniperZone;
